import { useState } from 'react';
import { ArrowLeftRight, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  FUND_TRANSACTION_TYPE_LABELS,
  type FundTransactionDetail,
  type ReconciliationStateResponse,
} from '@/types/fund-transactions';
import { formatCurrency, formatFxRate } from '@/utils/formatters';

import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';

/**
 * A conversion the bank executed, and the margin it revealed.
 *
 * Both amounts are shown as the bank moved them rather than one derived from
 * the other, because the gap between them and the ECB spot is the whole point:
 * it is what the ledger books to the bank-margin account instead of leaving in
 * the exchange-rate result. A margin is only measured on the way out - buying
 * currency carries it at what it cost, so there is nothing to separate.
 *
 * A draft can be deleted, because one read off a statement was recorded in a
 * single click.
 */
export function CurrencyConversionDetailsCard({
  transaction,
  fundName,
  fundCurrency,
  reconciliation,
  onViewJournalEntries,
  onDelete,
}: {
  transaction: FundTransactionDetail;
  fundName: string;
  fundCurrency: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
  onDelete: () => Promise<void>;
}) {
  const snapshot = transaction.currency_conversion;
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  return (
    <>
      <TransactionDetailsCard
        transaction={transaction}
        fundName={fundName}
        reconciliation={reconciliation}
        onViewJournalEntries={onViewJournalEntries}
        icon={<ArrowLeftRight className="h-5 w-5" />}
        iconClassName="bg-teal-100 text-teal-600 dark:bg-teal-950/40 dark:text-teal-300"
        eyebrow={FUND_TRANSACTION_TYPE_LABELS[transaction.transaction_type]}
        actions={
          transaction.status === 'DRAFT' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmingDelete(true)}
              data-testid="delete-conversion-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete draft
            </Button>
          )
        }
        fields={
          snapshot && (
            <>
              <Field label="Converted from">
                {formatCurrency(snapshot.from_amount, {
                  currency: snapshot.from_currency,
                })}
              </Field>
              <Field label="Converted to">
                {formatCurrency(snapshot.to_amount, {
                  currency: snapshot.to_currency,
                })}
              </Field>
              <Field label="Bank rate">
                {formatFxRate(snapshot.bank_rate)}
              </Field>
              <Field label="ECB spot">{formatFxRate(snapshot.spot_rate)}</Field>
              {snapshot.settlement_spread != null && (
                <Field label="Bank margin">
                  {formatCurrency(snapshot.settlement_spread, {
                    currency: fundCurrency,
                  })}
                </Field>
              )}
            </>
          )
        }
      />

      <AlertDialog
        open={isConfirmingDelete}
        onOpenChange={setIsConfirmingDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this draft conversion?</AlertDialogTitle>
            <AlertDialogDescription>
              Nothing has been posted to the ledger, so there is nothing to
              reverse. The bank lines it settled - both legs and any transfers
              that funded them - go back to the queue, and the trade is offered
              again on the fund's conversions page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep the draft</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
